import { Injectable } from '@angular/core';

import { Contact } from '../../models/contact';

import { BackendBaseService } from '../backend-base.service';
import { HttpClientService } from '../common/http-client.service';

@Injectable()
export class ContactService extends BackendBaseService {
  /**
   * Base URL of contacts resource
   * @type {String}
   */
  url = 'contacts';

  constructor(public httpClient: HttpClientService) {
    super(httpClient);
  }

  /**
   * Get bills (payments) of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with bills
   */
  bills(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/bills.json`, params);
  }

  /**
   * Get billings of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with billings
   */
  billings(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/billings.json`, params);
  }

  /**
   * Get bulk edit form select field lists and default options
   * @return {Promise<any>} Response with bulk edit options
   */
  bulkEditOptions(): Promise<any> {
    return this.httpClient.get(`${this.url}.json`, { bulk_edit_options: true });
  }

  /**
   * Clone contact
   * @param  {number}       contactId Id of contact
   * @param  {any}          params    Clone params
   * @return {Promise<any>}           Server response
   */
  clone(params: any): Promise<any> {
    return this.httpClient.post(`${this.url}/clone.json`, params);
  }

  contactInfo(contactId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}.json`, { contact_info: true });
  }

  contactSidebarInfo(contactId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}.json`, { contact_sidebar_info: true });
  }

  /**
   * Get disbursements (checks) of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with checks
   */
  disbursements(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/disbursements.json`, params);
  }

  /**
   * Get documents of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with documents
   */
  documents(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/documents.json`, params);
  }

  /**
   * Get employees of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with employees
   */
  employees(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/employees.json`, params);
  }

  findAddress(contactId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/find_address.json`, { angular: true, contact_id: contactId });
  }

  findPhone(contactId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/find_phone.json`, { angular: true, contact_id: contactId });
  }

  getPaymentAddresses(contact_id: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contact_id}.json`, { get_payment_addresses: true });
  }

  getVendingMatters(contact_id: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contact_id}.json`, { get_vending_matters: true });
  }

  htmlSrcdoc(contactId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/html_srcdoc.json`);
  }

  /**
   * Get matters of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with matters
   */
  matters(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/matters.json`, params);
  }

  /**
   * Merge contact
   * @param  {number}       contactId Id of contact
   * @param  {any}          params    Merge params
   * @return {Promise<any>}           Server response
   */
  merge(params: any): Promise<any> {
    return this.httpClient.post(`${this.url}/merge.json`, params);
  }

  /**
   * Get receipts of a contact with pagination
   * @param  {number}       contactId id of contact
   * @param  {any}          params    params for pagination and any other available search options
   * @return {Promise<any>}           Response with receipts
   */
  receipts(contactId: number, params: any): Promise<any> {
    return this.httpClient.get(`${this.url}/${contactId}/receipts.json`, params);
  }

  /**
   * CREATE and UPDATE actions for Contact resource
   * @param  {Contact} contact Contact to be saved
   * @return {Promise<any>} Save response
   */
  save(contact: Contact): Promise<any> {
    return super.save({
      contact: contact,
      id: contact.id
    });
  }
}
